import { type MouseEventHandler, useRef, useState } from 'react'

import { cn } from '@/lib/utils'

export const MAGNIFIER_SIZE = 200

export const ImageWithMagnifier = ({
  src,
  imgWidth = 0,
  imgHeight = 0,
  zoomLevel = 2,
  className = {},
}: {
  src: string
  imgWidth?: number
  imgHeight?: number
  zoomLevel?: number
  className?: {
    root?: string
    image?: string
    magnifier?: string
  }
}) => {
  const [showMagnifier, setShowMagnifier] = useState(false)
  const [position, setPosition] = useState({ bgRatioX: 0, bgRatioY: 0, mouseX: 0, mouseY: 0 })
  const imgRef = useRef<HTMLImageElement | null>(null)

  const handleMouseEnter = () => {
    setShowMagnifier(true)
  }

  const handleMouseLeave = () => {
    setShowMagnifier(false)
  }

  const handleMouseMove: MouseEventHandler<HTMLImageElement> = (e) => {
    if (!imgRef.current) return

    const { left, top, width: imageWidth, height: imageHeight } = imgRef.current.getBoundingClientRect()

    // Calculate mouse position relative to the image area
    const mouseX = e.clientX - left
    const mouseY = e.clientY - top

    // Calculate the ratio for the background position
    const bgRatioX = mouseX / imageWidth
    const bgRatioY = mouseY / imageHeight

    setPosition({
      bgRatioX,
      bgRatioY,
      mouseX,
      mouseY,
    })
  }

  return (
    <div className={cn('relative', 'w-fit', className.root)}>
      <img
        ref={imgRef}
        src={src}
        style={{
          width: imgWidth || '100%',
          height: imgHeight || 'auto',
          cursor: 'zoom-in',
        }}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onMouseMove={handleMouseMove}
        alt={`${src} document image`}
        className={className.image}
      />

      {showMagnifier && imgRef.current && (
        <div
          style={{
            position: 'absolute',
            pointerEvents: 'none',
            width: `${MAGNIFIER_SIZE}px`,
            height: `${MAGNIFIER_SIZE}px`,
            border: '2px solid #ccc',
            borderRadius: '4px',
            left: `${position.mouseX - MAGNIFIER_SIZE / 2}px`,
            top: `${position.mouseY - MAGNIFIER_SIZE / 2}px`,
            backgroundImage: `url("${src}")`,
            backgroundRepeat: 'no-repeat',
            backgroundSize: `${imgRef.current.offsetWidth * zoomLevel}px ${imgRef.current.offsetHeight * zoomLevel}px`,
            backgroundPosition: `
              ${-position.bgRatioX * zoomLevel * imgRef.current.offsetWidth + MAGNIFIER_SIZE / 2}px
              ${-position.bgRatioY * zoomLevel * imgRef.current.offsetHeight + MAGNIFIER_SIZE / 2}px
            `,
            zIndex: 10,
            display: 'block',
          }}
          className={className.magnifier}
        />
      )}
    </div>
  )
}
